import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { CustomerTag } from 'api/user-domain/user-domain.types';
import IncTextField from 'components/forms/fields/inc-text-field/inc-text-field.component';
import { useState, Fragment } from 'react';
import { UseFieldArrayReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';
import AutocompleteBase from '../fields/autocomplete-field/autocomplete-base/autocomplete-base.component';
import AutocompleteField from '../fields/autocomplete-field/autocomplete-field.component';
import { addTagsFormSchema } from './add-tags-form.schema';

const EditTagsForm = ({
  tags = [],
  customerTagFields,
}: {
  tags: CustomerTag[];
  customerTagFields: UseFieldArrayReturn<InferType<typeof addTagsFormSchema>>;
}) => {
  const { t } = useTranslation();
  const [selectedTag, setSelectedTag] = useState(null);

  const handleRemoveTag = (index: number) => {
    customerTagFields.remove(index);
  };

  const onChangCollectionValues = (event, value, index, key) => {
    const tag = tags.find(tag => tag.key === key);

    customerTagFields.update(index, {
      ...tag,
      value: value.id,
    });
  };

  return (
    <>
      <Typography gutterBottom variant="h3">
        {t('page.customers.form.addTags.addNewTag')}
      </Typography>
      <AutocompleteBase
        options={tags
          .filter(
            tag =>
              !customerTagFields.fields?.some(cTag => cTag.key === tag.key),
          )
          .map(tag => ({ id: tag.key, label: tag.title }))}
        value={selectedTag}
        onChange={(_e, option) => {
          setSelectedTag(option.id);
        }}
        data-testid={'TagsForm.IncTextField.Tag'}
      />
      <Button
        fullWidth
        size="small"
        onClick={() => {
          const tag = tags.find(tag => tag.key === selectedTag);
          if (customerTagFields.fields.length > 0) {
            customerTagFields.prepend({
              ...tag,
              value: '',
            });
          } else {
            customerTagFields.update(0, {
              ...tag,
              value: '',
            });
          }
          setSelectedTag('');
        }}
        sx={{ textTransform: 'capitalize' }}
        data-testid={'TagsForm.Button.AddNewTag'}
        disabled={!selectedTag}
      >
        {t('page.customers.form.addTags.addNewTag')}
      </Button>
      {customerTagFields.fields.length > 0 && (
        <Typography gutterBottom variant="h3" sx={{ marginTop: '20px' }}>
          {t('page.customers.form.addTags.editTags')}
        </Typography>
      )}
      {customerTagFields.fields.map((tag, index) => {
        return (
          <Fragment key={tag.id}>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              key={tag.id}
              data-testid="AddTagsForm.TagRow"
            >
              {tag?.attributeType === 'COLLECTION' ? (
                <>
                  <AutocompleteField
                    key={tag?.key}
                    name={`tags.[${index}].value`}
                    options={tag.collectionValues.map(option => ({
                      id: option,
                      label: option,
                    }))}
                    value={tag?.value}
                    label={tag.title}
                    data-testid={`AddTagsForm.AutocompleteField.${tag.key}`}
                    onChange={(event, value) =>
                      onChangCollectionValues(event, value, index, tag?.key)
                    }
                  />
                  <IconButton
                    data-testid="TagsForm.AutocompleteField.closeButton"
                    onClick={() => handleRemoveTag(index)}
                    sx={{ ml: 1, mt: 1 }}
                  >
                    <CloseOutlinedIcon sx={{ color: 'error.main' }} />
                  </IconButton>
                </>
              ) : (
                <>
                  <IncTextField
                    key={tag.id}
                    name={`tags.[${index}].value`}
                    label={tag.title}
                    data-testid={`AddTagsForm.IncTextField.${tag.key}`}
                  />
                  <IconButton
                    data-testid="TagsForm.IncTextField.closeButton"
                    onClick={() => handleRemoveTag(index)}
                    sx={{ ml: 1, mt: 1 }}
                  >
                    <CloseOutlinedIcon sx={{ color: 'error.main' }} />
                  </IconButton>
                </>
              )}
            </Box>
          </Fragment>
        );
      })}
    </>
  );
};

export default EditTagsForm;
